import { useState, useEffect } from "react";
import { createOrder, getOrderDetails } from "@/shared/api/orders/api-orders";
import { getPaymentInfo } from "@/shared/api/payment";
import { useTableStore } from "@/shared/stores/tableStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { useAuthStore } from "@/shared/stores/authStore";
import { classifyApiError } from "@/shared/lib/apiErrorHandler";

declare global {
  interface Window {
    YooMoneyCheckoutWidget: new (options: {
      confirmation_token: string;
      customization?: {
        payment_methods: string[];
      };
      error_callback: (error: unknown) => void;
      success_callback?: () => void;
    }) => {
      render: (containerId: string) => void;
      destroy: () => void;
      // Метод есть в API виджета и вызывается ниже; в объявлении его не было,
      // и это скрывалось за `checkoutWidget: any`.
      on: (event: string, handler: () => void) => void;
    };
  }
}

/** Экземпляр виджета — выводим из объявления конструктора, чтобы типы не разъезжались. */
type CheckoutWidget = InstanceType<Window["YooMoneyCheckoutWidget"]>;

export const useYooKassa = (
  selectedPaymentMethod: string | null,
  tariffId: number | null,
  promocode: string | null,
  email: string | null,
  supplierId: string | null,
  onRender?: () => void,
  onError?: (message: string) => void,
  onSuccess?: () => void,
) => {
  const { tableId } = useTableStore();
  const { serviceType } = useServiceWorkflowStore();
  const { profile } = useAuthStore();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://yookassa.ru/checkout-widget/v1/checkout-widget.js";
    script.async = true;
    script.onload = () => {
      setIsScriptLoaded(true);
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (
      !selectedPaymentMethod ||
      !isScriptLoaded ||
      !tariffId ||
      !email ||
      !supplierId ||
      !tableId
    ) {
      return;
    }

    let checkoutWidget: CheckoutWidget | undefined;

    const processPayment = async () => {
      try {
        const customer_id = profile?.id;

        if (!customer_id) {
          const errorMsg =
            "Не удалось определить ID пользователя. Оплата невозможна.";
          console.error(errorMsg);
          if (onError) {
            onError(errorMsg);
          }
          return;
        }

        if (!serviceType) return;

        const orderPayload = {
          customer_email: email,
          tariff_id: tariffId,
          services: [
            {
              service_type: serviceType,
              service_id: tableId,
            },
          ],
          save_payment_method: true,
          start_subscription: true,
          promocode_code: promocode ?? null,
          saved_payment_method_id: null,
          redirect_return_url: window.location.href,
        };

        const order = await createOrder(orderPayload);
        const orderDetails = await getOrderDetails(order.order_id);
        const paymentInfo = await getPaymentInfo(orderDetails.payment.id);

        checkoutWidget = new window.YooMoneyCheckoutWidget({
          confirmation_token: paymentInfo.confirmation.confirmation_token,
          error_callback: function (error: unknown) {
            console.error("YooKassa widget error:", error);
            if (onError) {
              onError("Ошибка платежного виджета. Попробуйте позже.");
            }
          },
        });
        // onSuccess опционален, как onError и onRender рядом. Прежний
        // `checkoutWidget: any` скрывал, что при его отсутствии в виджет
        // уходил undefined вместо колбэка.
        if (onSuccess) {
          checkoutWidget.on("success", onSuccess);
        }

        checkoutWidget.render("payment-form");
        if (onRender) {
          onRender();
        }
      } catch (error) {
        console.error("Payment processing error:", error);
        const classified = classifyApiError(error);
        if (onError) {
          onError(classified.message);
        }
      }
    };

    processPayment();

    return () => {
      if (checkoutWidget) {
        checkoutWidget.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPaymentMethod,
    isScriptLoaded,
    tariffId,
    promocode,
    email,
    supplierId,
    tableId,
  ]);
};
